import Phaser from 'phaser'
import { FRUITS, type Fruit } from '../fruits'
import { mixColors, blendColors } from '../colorMix'

const JUICER_W = 110
const JUICER_H = 150
const GLASS_W = 100
const GLASS_H = 170
const FONT_STACK = '"PingFang SC", "Microsoft YaHei", system-ui, sans-serif'
const MAX_FRUITS = 12

export default class MainScene extends Phaser.Scene {
  private addedColors: number[] = []
  private juicerBody!: Phaser.GameObjects.Container
  private juicerContents!: Phaser.GameObjects.Graphics
  private juicerContentsGlow!: Phaser.GameObjects.Graphics
  private juicerFruits: Phaser.GameObjects.Text[] = []
  private glass!: Phaser.GameObjects.Container
  private glassFill!: Phaser.GameObjects.Graphics
  private glassFillLevel = 0
  private glassColor = 0xffffff
  private countText!: Phaser.GameObjects.Text
  private statusText!: Phaser.GameObjects.Text
  private statusTimer?: Phaser.Time.TimerEvent
  private juicing = false

  // liquid-ready state
  private juicerHasLiquid = false
  private juicerLiquidColor = 0xffffff
  private juicerLevel = 0 // 0..1

  // fruit drag
  private draggedFruit?: { ghost: Phaser.GameObjects.Text; fruit: Fruit }
  private juicerHoverHighlight = false

  // juicer drag
  private draggingJuicer = false
  private juicerDragOffset = { x: 0, y: 0 }

  private readonly juicerX = 360
  private readonly juicerY = 410
  private readonly glassX = 680
  private readonly glassY = 470

  constructor() {
    super('MainScene')
  }

  create() {
    this.drawBackground()
    this.drawTitle()
    this.createFruitShelf()
    this.createJuicer()
    this.createGlass()
    this.createButtons()

    this.countText = this.add
      .text(this.juicerX, this.juicerY + 130, '榨汁机里：0 个水果', {
        fontFamily: FONT_STACK,
        fontSize: '20px',
        color: '#5a432a',
      })
      .setOrigin(0.5)

    this.statusText = this.add
      .text(this.scale.width / 2, this.scale.height - 24, '把水果拖进榨汁机 → 按「榨汁！」→ 把榨汁机拖到杯子上', {
        fontFamily: FONT_STACK,
        fontSize: '18px',
        color: '#8a6a4a',
      })
      .setOrigin(0.5)

    this.input.on('pointermove', this.handlePointerMove, this)
    this.input.on('pointerup', this.handlePointerUp, this)
    this.input.on('pointerupoutside', this.handlePointerUp, this)
  }

  private drawBackground() {
    const W = this.scale.width
    const H = this.scale.height
    const g = this.add.graphics()
    g.fillGradientStyle(0xfff3d6, 0xfff3d6, 0xffe0b5, 0xffe0b5, 1)
    g.fillRect(0, 0, W, H)

    const deco = [
      { emoji: '🍓', x: 50, y: 60, size: 30 },
      { emoji: '🍊', x: W - 50, y: 80, size: 34 },
      { emoji: '🍇', x: 36, y: H - 50, size: 30 },
      { emoji: '🥝', x: W - 40, y: H - 60, size: 28 },
    ]
    for (const d of deco) {
      this.add
        .text(d.x, d.y, d.emoji, { fontSize: `${d.size}px` })
        .setOrigin(0.5)
        .setAlpha(0.55)
    }
  }

  private drawTitle() {
    const W = this.scale.width
    this.add
      .text(W / 2, 50, '🥤 水 果 榨 汁 机 🥤', {
        fontFamily: FONT_STACK,
        fontSize: '42px',
        color: '#c94c4c',
        fontStyle: 'bold',
        stroke: '#ffffff',
        strokeThickness: 4,
      })
      .setOrigin(0.5)

    this.add
      .text(W / 2, 95, '拖水果进榨汁机 → 按「榨汁！」→ 榨汁机拖到杯子倒进去 ✨', {
        fontFamily: FONT_STACK,
        fontSize: '18px',
        color: '#7a5a3a',
      })
      .setOrigin(0.5)
  }

  private isBusy(): boolean {
    return this.juicing
  }

  private createFruitShelf() {
    FRUITS.forEach((fruit, i) => {
      const x = 150 + i * 132
      const y = 180
      this.makeFruitButton(fruit, x, y)
    })
  }

  private makeFruitButton(fruit: Fruit, x: number, y: number) {
    const c = this.add.container(x, y)

    const bg = this.add.graphics()
    bg.fillStyle(0xffffff, 1)
    bg.fillRoundedRect(-52, -55, 104, 110, 14)
    bg.lineStyle(3, 0xdda15e, 1)
    bg.strokeRoundedRect(-52, -55, 104, 110, 14)

    const swatch = this.add.graphics()
    swatch.fillStyle(fruit.color, 1)
    swatch.fillRoundedRect(-22, -48, 44, 8, 4)

    const emoji = this.add.text(0, -5, fruit.emoji, { fontSize: '56px' }).setOrigin(0.5)

    const label = this.add
      .text(0, 40, fruit.label, {
        fontFamily: FONT_STACK,
        fontSize: '16px',
        color: '#7a5a3a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)

    c.add([bg, swatch, emoji, label])
    c.setSize(104, 110)
    c.setInteractive({ useHandCursor: true })

    c.on('pointerover', () => {
      if (this.draggedFruit) return
      this.tweens.add({ targets: c, scale: 1.08, duration: 120, ease: 'Quad.easeOut' })
    })
    c.on('pointerout', () =>
      this.tweens.add({ targets: c, scale: 1.0, duration: 120 }),
    )
    c.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.isBusy() || this.juicerHasLiquid || this.draggedFruit) return
      this.tweens.add({ targets: c, scale: 0.95, duration: 80, yoyo: true })
      this.startFruitDrag(fruit, pointer.worldX, pointer.worldY)
    })
  }

  private startFruitDrag(fruit: Fruit, x: number, y: number) {
    const ghost = this.add
      .text(x, y, fruit.emoji, { fontSize: '56px' })
      .setOrigin(0.5)
      .setDepth(1000)
    ghost.setAlpha(0.92)
    this.tweens.add({ targets: ghost, scale: 1.15, duration: 120, ease: 'Back.easeOut' })
    this.draggedFruit = { ghost, fruit }
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer) {
    if (this.draggedFruit) {
      this.draggedFruit.ghost.x = pointer.worldX
      this.draggedFruit.ghost.y = pointer.worldY
      this.setJuicerHoverHighlight(this.isOverJuicer(pointer.worldX, pointer.worldY))
      return
    }
    if (this.draggingJuicer) {
      this.juicerBody.x = pointer.worldX + this.juicerDragOffset.x
      this.juicerBody.y = pointer.worldY + this.juicerDragOffset.y
      const nearGlass = this.isNearGlassPourSpot(this.juicerBody.x, this.juicerBody.y)
      this.juicerBody.angle = nearGlass ? 18 : 0
    }
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer) {
    if (this.draggedFruit) {
      const { ghost, fruit } = this.draggedFruit
      this.draggedFruit = undefined
      this.setJuicerHoverHighlight(false)
      if (this.isBusy() || this.juicerHasLiquid) {
        this.fadeAwayGhost(ghost)
        return
      }
      if (this.isOverJuicer(pointer.worldX, pointer.worldY)) {
        this.dropIntoJuicer(fruit, ghost)
      } else {
        this.fadeAwayGhost(ghost)
      }
      return
    }
    if (this.draggingJuicer) {
      this.draggingJuicer = false
      if (this.isNearGlassPourSpot(this.juicerBody.x, this.juicerBody.y)) {
        this.pourFromJuicerIntoGlass()
      } else {
        this.returnJuicerToOrigin()
      }
    }
  }

  private fadeAwayGhost(ghost: Phaser.GameObjects.Text) {
    this.tweens.add({
      targets: ghost,
      alpha: 0,
      scale: 0.4,
      duration: 220,
      ease: 'Quad.easeIn',
      onComplete: () => ghost.destroy(),
    })
  }

  private isOverJuicer(x: number, y: number): boolean {
    const cx = this.juicerBody.x
    const cy = this.juicerBody.y
    return (
      x >= cx - JUICER_W / 2 - 20 &&
      x <= cx + JUICER_W / 2 + 20 &&
      y >= cy - JUICER_H / 2 - 55 &&
      y <= cy + JUICER_H / 2 + 25
    )
  }

  private isNearGlassPourSpot(x: number, y: number): boolean {
    // sweet spot: juicer slightly left-above glass so its spout sits over glass
    const targetX = this.glassX - 70
    const targetY = this.glassY - 30
    return Math.hypot(x - targetX, y - targetY) < 110
  }

  private setJuicerHoverHighlight(over: boolean) {
    if (over === this.juicerHoverHighlight) return
    this.juicerHoverHighlight = over
    this.tweens.add({
      targets: this.juicerBody,
      scale: over ? 1.06 : 1.0,
      duration: 150,
      ease: 'Quad.easeOut',
    })
  }

  private dropIntoJuicer(fruit: Fruit, ghost: Phaser.GameObjects.Text) {
    if (this.juicerFruits.length >= MAX_FRUITS) {
      this.fadeAwayGhost(ghost)
      this.flashStatus('榨汁机满啦！先榨一下吧 🌀')
      return
    }

    const idx = this.juicerFruits.length
    const col = idx % 3
    const row = Math.floor(idx / 3)
    const localX = (col - 1) * 30 + Phaser.Math.Between(-5, 5)
    const localY = JUICER_H / 2 - 20 - row * 26 + Phaser.Math.Between(-3, 3)
    const worldX = this.juicerBody.x + localX
    const worldY = this.juicerBody.y + localY

    this.tweens.add({
      targets: ghost,
      x: worldX,
      y: worldY,
      scale: 0.55,
      duration: 320,
      ease: 'Bounce.easeOut',
      onComplete: () => {
        ghost.destroy()
        const emoji = this.add
          .text(localX, localY, fruit.emoji, { fontSize: '34px' })
          .setOrigin(0.5)
          .setAngle(Phaser.Math.Between(-20, 20))
        this.juicerBody.add(emoji)
        this.juicerFruits.push(emoji)
        this.addedColors.push(fruit.color)
        this.pulseJuicer()
        this.countText.setText(`榨汁机里：${this.addedColors.length} 个水果`)
      },
    })
  }

  private createJuicer() {
    const c = this.add.container(this.juicerX, this.juicerY)
    this.juicerBody = c

    const shadow = this.add.graphics()
    shadow.fillStyle(0x000000, 0.14)
    shadow.fillEllipse(6, JUICER_H / 2 + 22, JUICER_W + 50, 16)

    const base = this.add.graphics()
    base.fillStyle(0x7a5a3a, 1)
    base.fillRoundedRect(-JUICER_W / 2 - 18, JUICER_H / 2 - 2, JUICER_W + 36, 22, 6)

    const top = this.add.graphics()
    top.fillStyle(0xd94f4f, 1)
    top.fillRoundedRect(-JUICER_W / 2 - 6, -JUICER_H / 2 - 40, JUICER_W + 12, 42, 10)
    top.fillStyle(0x333333, 1)
    top.fillRoundedRect(-20, -JUICER_H / 2 - 52, 40, 14, 4)
    top.fillStyle(0xffd93d, 1)
    top.fillCircle(JUICER_W / 2 - 8, -JUICER_H / 2 - 19, 6)

    const spout = this.add.graphics()
    spout.fillStyle(0xd94f4f, 1)
    spout.fillTriangle(
      JUICER_W / 2 - 2, 30,
      JUICER_W / 2 + 26, 38,
      JUICER_W / 2 + 26, 58,
    )
    spout.fillRect(JUICER_W / 2 + 22, 50, 10, 14)

    const body = this.add.graphics()
    body.fillStyle(0xffffff, 0.35)
    body.fillRoundedRect(-JUICER_W / 2, -JUICER_H / 2, JUICER_W, JUICER_H, 10)

    this.juicerContentsGlow = this.add.graphics()
    this.juicerContents = this.add.graphics()

    const frame = this.add.graphics()
    frame.lineStyle(4, 0xc94c4c, 1)
    frame.strokeRoundedRect(-JUICER_W / 2, -JUICER_H / 2, JUICER_W, JUICER_H, 10)
    frame.fillStyle(0xffffff, 0.45)
    frame.fillRoundedRect(-JUICER_W / 2 + 6, -JUICER_H / 2 + 8, 6, JUICER_H - 20, 3)

    c.add([
      shadow,
      base,
      top,
      spout,
      body,
      this.juicerContentsGlow,
      this.juicerContents,
      frame,
    ])

    c.setInteractive(
      new Phaser.Geom.Rectangle(-JUICER_W / 2 - 20, -JUICER_H / 2 - 55, JUICER_W + 40, JUICER_H + 80),
      Phaser.Geom.Rectangle.Contains,
    )
    c.on('pointerover', () => {
      if (this.juicerHasLiquid && !this.juicing && !this.draggingJuicer) {
        this.input.setDefaultCursor('grab')
      }
    })
    c.on('pointerout', () => this.input.setDefaultCursor('default'))
    c.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.juicerHasLiquid || this.juicing || this.draggedFruit) return
      this.startJuicerDrag(pointer)
    })
  }

  private startJuicerDrag(pointer: Phaser.Input.Pointer) {
    this.draggingJuicer = true
    this.juicerDragOffset = {
      x: this.juicerBody.x - pointer.worldX,
      y: this.juicerBody.y - pointer.worldY,
    }
    this.input.setDefaultCursor('grabbing')
    this.juicerBody.setDepth(500)
    this.tweens.add({
      targets: this.juicerBody,
      scale: 1.08,
      duration: 140,
      ease: 'Back.easeOut',
    })
  }

  private returnJuicerToOrigin() {
    this.input.setDefaultCursor('default')
    this.tweens.add({
      targets: this.juicerBody,
      x: this.juicerX,
      y: this.juicerY,
      angle: 0,
      scale: 1,
      duration: 420,
      ease: 'Back.easeOut',
      onComplete: () => this.juicerBody.setDepth(0),
    })
  }

  private drawJuicerLiquid(color: number, level: number) {
    this.juicerContents.clear()
    this.juicerContentsGlow.clear()
    if (level <= 0) return
    const fillH = (JUICER_H - 16) * level
    const x0 = -JUICER_W / 2 + 6
    const y0 = JUICER_H / 2 - 8 - fillH
    const w = JUICER_W - 12

    this.juicerContentsGlow.fillStyle(color, 0.25)
    this.juicerContentsGlow.fillRoundedRect(x0 - 2, y0 - 2, w + 4, fillH + 4, 8)

    this.juicerContents.fillStyle(color, 0.9)
    this.juicerContents.fillRoundedRect(x0, y0, w, fillH, 6)
    this.juicerContents.fillStyle(0xffffff, 0.3)
    this.juicerContents.fillRoundedRect(x0, y0, w, 5, 2)
  }

  private pulseJuicer() {
    this.tweens.add({
      targets: this.juicerBody,
      scaleX: 1.04,
      scaleY: 0.96,
      duration: 110,
      yoyo: true,
      ease: 'Quad.easeOut',
    })
  }

  private createGlass() {
    const c = this.add.container(this.glassX, this.glassY)
    this.glass = c

    const shadow = this.add.graphics()
    shadow.fillStyle(0x000000, 0.14)
    shadow.fillEllipse(0, GLASS_H / 2 + 14, GLASS_W + 30, 14)

    const back = this.add.graphics()
    back.fillStyle(0xffffff, 0.35)
    back.fillRoundedRect(-GLASS_W / 2, -GLASS_H / 2, GLASS_W, GLASS_H, 12)

    this.glassFill = this.add.graphics()

    const frame = this.add.graphics()
    frame.lineStyle(4, 0x88b5c2, 1)
    frame.strokeRoundedRect(-GLASS_W / 2, -GLASS_H / 2, GLASS_W, GLASS_H, 12)
    frame.lineStyle(3, 0x88b5c2, 1)
    frame.strokeEllipse(0, -GLASS_H / 2, GLASS_W, 14)
    frame.fillStyle(0xffffff, 0.5)
    frame.fillRoundedRect(-GLASS_W / 2 + 8, -GLASS_H / 2 + 10, 6, GLASS_H - 24, 3)

    const straw = this.add.graphics()
    straw.fillStyle(0xff7a8c, 1)
    straw.fillRect(GLASS_W / 2 - 22, -GLASS_H / 2 - 34, 8, 70)
    straw.fillStyle(0xffffff, 1)
    straw.fillRect(GLASS_W / 2 - 20, -GLASS_H / 2 - 24, 4, 18)

    c.add([shadow, back, this.glassFill, frame, straw])
  }

  private redrawGlass() {
    this.glassFill.clear()
    if (this.glassFillLevel <= 0) return
    const innerW = GLASS_W - 12
    const innerH = GLASS_H - 16
    const h = innerH * this.glassFillLevel
    const x0 = -innerW / 2
    const y0 = GLASS_H / 2 - 8 - h

    this.glassFill.fillStyle(this.glassColor, 0.92)
    this.glassFill.fillRoundedRect(x0, y0, innerW, h, 6)
    this.glassFill.fillStyle(0xffffff, 0.35)
    this.glassFill.fillRoundedRect(x0, y0, innerW, 5, 2)
  }

  private createButtons() {
    this.makeButton(this.juicerX, 610, '榨汁！', 0xff7a00, () => this.juice())
    this.makeButton(this.glassX, 610, '倒掉杯子', 0x7a8b9c, () => this.emptyGlass())
  }

  private makeButton(x: number, y: number, label: string, color: number, onClick: () => void) {
    const c = this.add.container(x, y)

    const bg = this.add.graphics()
    bg.fillStyle(0x000000, 0.18)
    bg.fillRoundedRect(-85, -24, 170, 56, 14)
    bg.fillStyle(color, 1)
    bg.fillRoundedRect(-85, -28, 170, 56, 14)
    bg.lineStyle(3, 0x333333, 1)
    bg.strokeRoundedRect(-85, -28, 170, 56, 14)

    const txt = this.add
      .text(0, 0, label, {
        fontFamily: FONT_STACK,
        fontSize: '26px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#00000055',
        strokeThickness: 2,
      })
      .setOrigin(0.5)

    c.add([bg, txt])
    c.setSize(170, 56)
    c.setInteractive({ useHandCursor: true })

    c.on('pointerover', () =>
      this.tweens.add({ targets: c, scale: 1.05, duration: 100 }),
    )
    c.on('pointerout', () =>
      this.tweens.add({ targets: c, scale: 1.0, duration: 100 }),
    )
    c.on('pointerdown', () => {
      this.tweens.add({ targets: c, scale: 0.95, duration: 80, yoyo: true })
      onClick()
    })
  }

  private juice() {
    if (this.juicing) return
    if (this.juicerHasLiquid) {
      this.flashStatus('榨汁机里还有汁水！先拖到杯子倒掉 🥤')
      return
    }
    if (this.addedColors.length === 0) {
      this.flashStatus('先把水果拖进榨汁机吧~ 🍓🍊')
      return
    }

    this.juicing = true
    this.flashStatus('咔哒咔哒…正在榨汁！🌀')

    const mixColor = mixColors(this.addedColors)
    const baseX = this.juicerBody.x

    this.juicerFruits.forEach((f) => {
      this.tweens.add({
        targets: f,
        angle: f.angle + Phaser.Math.Between(-360, 360),
        duration: 700,
        ease: 'Sine.easeInOut',
      })
      this.tweens.add({
        targets: f,
        x: f.x + Phaser.Math.Between(-12, 12),
        y: f.y + Phaser.Math.Between(-10, 10),
        duration: 100,
        yoyo: true,
        repeat: 6,
      })
    })

    this.tweens.add({
      targets: this.juicerBody,
      x: baseX + 6,
      duration: 45,
      yoyo: true,
      repeat: 14,
      onComplete: () => {
        this.juicerBody.x = baseX
        this.pulverizeIntoLiquid(mixColor)
      },
    })
  }

  private pulverizeIntoLiquid(mixColor: number) {
    this.tweens.add({
      targets: this.juicerFruits,
      alpha: 0,
      scale: 0.15,
      duration: 260,
      ease: 'Quad.easeIn',
      onComplete: () => {
        this.juicerFruits.forEach((f) => f.destroy())
        this.juicerFruits = []
        this.addedColors = []

        this.juicerContents.setAlpha(0)
        this.juicerContentsGlow.setAlpha(0)
        this.drawJuicerLiquid(mixColor, 1.0)
        this.juicerLevel = 1.0
        this.juicerLiquidColor = mixColor
        this.juicerHasLiquid = true

        this.tweens.add({
          targets: [this.juicerContents, this.juicerContentsGlow],
          alpha: 1,
          duration: 320,
          onComplete: () => {
            this.juicing = false
            this.countText.setText('榨好啦！拖榨汁机到杯子里 →')
            this.flashStatus('榨好了！把榨汁机拖到杯子上方倒进去 🥤')
          },
        })
      },
    })
  }

  private pourFromJuicerIntoGlass() {
    if (!this.juicerHasLiquid) {
      this.returnJuicerToOrigin()
      return
    }
    if (this.glassFillLevel >= 1) {
      this.flashStatus('杯子满啦！先倒掉再继续 🥤')
      this.returnJuicerToOrigin()
      return
    }

    this.juicing = true
    this.input.setDefaultCursor('default')

    // move juicer to ideal pour position
    const pourX = this.glassX - 70
    const pourY = this.glassY - 30
    this.tweens.add({
      targets: this.juicerBody,
      x: pourX,
      y: pourY,
      angle: 22,
      scale: 1,
      duration: 220,
      ease: 'Quad.easeOut',
      onComplete: () => this.streamPour(),
    })
  }

  private streamPour() {
    const color = this.juicerLiquidColor
    const total = 10
    const perDrop = Math.min((1 - this.glassFillLevel) / total, 1 / 14)
    let done = 0

    const startLevel = this.juicerLevel
    this.tweens.addCounter({
      from: startLevel,
      to: 0,
      duration: total * 90 + 200,
      onUpdate: (tween) => {
        this.juicerLevel = tween.getValue() ?? 0
        this.drawJuicerLiquid(color, this.juicerLevel)
      },
    })

    const glassTopX = this.glassX
    const glassTopY = this.glassY - GLASS_H / 2 + 14

    this.time.addEvent({
      delay: 90,
      repeat: total - 1,
      callback: () => {
        // spout tip in world coords, accounting for juicer rotation
        const localX = JUICER_W / 2 + 26
        const localY = 58
        const rad = Phaser.Math.DegToRad(this.juicerBody.angle)
        const spoutX = this.juicerBody.x + localX * Math.cos(rad) - localY * Math.sin(rad)
        const spoutY = this.juicerBody.y + localX * Math.sin(rad) + localY * Math.cos(rad)

        const drop = this.add.circle(spoutX, spoutY, 7, color, 1)
        drop.setStrokeStyle(2, 0xffffff, 0.6)

        this.tweens.add({
          targets: drop,
          x: glassTopX,
          y: glassTopY,
          duration: 260,
          ease: 'Quad.easeIn',
          onComplete: () => {
            drop.destroy()
            this.splash(glassTopX, glassTopY, color)
            this.addToGlass(color, perDrop)
            done++
            if (done === total) this.finishPour()
          },
        })
      },
    })
  }

  private finishPour() {
    this.juicerHasLiquid = false
    this.juicerLevel = 0
    this.drawJuicerLiquid(0, 0)

    this.tweens.add({
      targets: this.juicerBody,
      angle: 0,
      duration: 220,
      ease: 'Quad.easeInOut',
    })
    this.tweens.add({
      targets: this.juicerBody,
      x: this.juicerX,
      y: this.juicerY,
      duration: 420,
      delay: 100,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.juicerBody.setDepth(0)
        this.juicing = false
        this.countText.setText('榨汁机里：0 个水果')
        this.flashStatus('哗啦~ 倒进杯子啦！再来一杯？✨')
      },
    })
  }

  private splash(x: number, y: number, color: number) {
    for (let i = 0; i < 4; i++) {
      const p = this.add.circle(x, y, 3, color, 1)
      const dx = (Math.random() - 0.5) * 40
      const dy = -10 - Math.random() * 20
      this.tweens.add({
        targets: p,
        x: x + dx,
        y: y + dy,
        alpha: 0,
        scale: 0.3,
        duration: 350,
        ease: 'Quad.easeOut',
        onComplete: () => p.destroy(),
      })
    }
  }

  private addToGlass(newColor: number, increment: number) {
    if (this.glassFillLevel <= 0) {
      this.glassColor = newColor
    } else {
      this.glassColor = blendColors(this.glassColor, this.glassFillLevel, newColor, increment)
    }
    this.glassFillLevel = Math.min(this.glassFillLevel + increment, 1)
    this.redrawGlass()
  }

  private emptyGlass() {
    if (this.juicing) return
    if (this.glassFillLevel <= 0) {
      this.flashStatus('杯子本来就是空的呀 😄')
      return
    }
    this.tweens.add({
      targets: this.glass,
      angle: -18,
      duration: 220,
      yoyo: true,
      ease: 'Quad.easeInOut',
      onComplete: () => (this.glass.angle = 0),
    })
    this.tweens.add({
      targets: this.glassFill,
      alpha: 0,
      duration: 300,
      onComplete: () => {
        this.glassFillLevel = 0
        this.glassColor = 0xffffff
        this.redrawGlass()
        this.glassFill.alpha = 1
        this.flashStatus('哗啦~ 倒光啦！')
      },
    })
  }

  private flashStatus(msg: string) {
    this.statusText.setText(msg)
    this.statusText.setAlpha(1)
    this.statusTimer?.remove()
    this.statusTimer = this.time.delayedCall(3000, () => {
      this.tweens.add({ targets: this.statusText, alpha: 0, duration: 400 })
    })
  }
}
